/**
 * Same-origin `?next=` redirect handling for the auth flow.
 *
 * A signed-out reader who follows a docs deep link (`/link/<target>`) is
 * bounced to `/login?next=<path>`; sign-in and email verification then send
 * them on to `<path>` instead of the default `/`. `next` is
 * attacker-influenceable — it rides in a URL a docs page hands out — so every
 * value is narrowed to a root-relative in-app path before anything navigates
 * to it. `?next=https://evil.example` must never become an open redirect.
 */

const NEXT_PARAM = "next";

// Any absolute or protocol-relative URL resolves to an origin other than this
// sentinel, so a smuggled `next` fails the origin check below. The value is
// arbitrary — it only has to be a fixed, valid base.
const SAME_ORIGIN_SENTINEL = "http://localhost";

/**
 * Narrow an arbitrary `next` value to a safe in-app path, or null.
 *
 * Accepts only root-relative paths that stay on this origin. Rejects absolute
 * URLs, protocol-relative `//host`, and the backslash variants (`/\host`,
 * which several browsers normalise back into `//host`).
 */
export function sanitizeInternalPath(
  raw: string | null | undefined
): string | null {
  if (!raw?.startsWith("/")) {
    return null;
  }
  // Protocol-relative (`//host`) and backslash-smuggled authorities (`/\host`)
  // start with a slash yet escape the origin, so reject them before parsing.
  if (raw.startsWith("//") || raw.startsWith("/\\")) {
    return null;
  }
  try {
    const url = new URL(raw, SAME_ORIGIN_SENTINEL);
    if (url.origin !== SAME_ORIGIN_SENTINEL) {
      return null;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

/** The validated `?next=` target from a `location.search`, or null. */
export function safeNextFromSearch(search: string): string | null {
  return sanitizeInternalPath(new URLSearchParams(search).get(NEXT_PARAM));
}

/**
 * `/login` carrying the current in-app destination as `?next=`, so sign-in can
 * return the reader there. Falls back to a bare `/login` when there's nothing
 * worth preserving (the root, or an unsafe/off-origin path).
 */
export function loginWithNext(dest: string): string {
  const safe = sanitizeInternalPath(dest);
  if (!safe || safe === "/") {
    return "/login";
  }
  return `/login?${NEXT_PARAM}=${encodeURIComponent(safe)}`;
}
